import ReactJson from "@microlink/react-json-view";
import { Tooltip } from "components/ui/tooltip";
import { AlertTriangle, Check, CheckCircle2, Copy, Edit2, FileText, LayoutDashboard, Maximize2, MessageCircle, Minimize2, Settings, User, XCircle } from "lucide-react";
import * as React from "react";
import { cn } from "src/common/utils";
import { MemoryAttributionFeedbackType, useSubmitMemoryAttributionFeedbackMutation } from "src/pages/AskPage/hooks/useSubmitMemoryAttributionFeedbackMutation";

import type { MemoryAttribution, MemoryBody } from "./types/memory-types";

interface ReferencedMemoriesSectionProps {
  memoryAttributions: ReadonlyArray<MemoryAttribution>;
  conversationId?: string;
  messageId?: string;
  onEditMemory?: (memoryId: string, memoryBody: MemoryBody) => void;
  isV2?: boolean;  // If true, this is from LlmConversation/LlmMessage (V2)
  activeMemoryId?: string | null;
  onMemoryClick?: (memoryId: string) => void;
}

// Helper to get source icon and label based on source type enum
export const getSourceIconFromType = (sourceType: string): { icon: React.ReactNode; label: string } => {
  // Map source type enum values to icons and labels
  switch (sourceType) {
    case "UI":
    case "TOOL_ADD":
    case "SLACK":
    case "DISCORD":
    case "ALERT_THREAD":
      return {
        icon: <MessageCircle className="w-4 h-4 text-green-400 relative" style={{ top: "3px" }} />,
        label: "Learned from conversations with your team"
      };
    case "UI_ADD":
    case "CHROME_EXTENSION":
      return {
        icon: <User className="w-4 h-4 text-cyan-400 relative" style={{ top: "3px" }} />,
        label: "Created by users in the memory explorer"
      };
    case "DASHBOARD":
      return {
        icon: <LayoutDashboard className="w-4 h-4 text-purple-400 relative" style={{ top: "3px" }} />,
        label: "Learned from dashboards or notebooks"
      };
    case "DOCUMENT":
      return {
        icon: <FileText className="w-4 h-4 text-primary relative" style={{ top: "3px" }} />,
        label: "Learned from documents"
      };
    case "INCIDENT_CHANNEL":
      return {
        icon: <AlertTriangle className="w-4 h-4 text-orange-400 relative" style={{ top: "3px" }} />,
        label: "Learned during incident response"
      };
    case "SYSTEM":
    case "ONBOARDING":
    default:
      return {
        icon: <Settings className="w-4 h-4 text-slate-400 relative" style={{ top: "3px" }} />,
        label: "System"
      };
  }
};

const MemorySourceDisplay: React.FC<{ source: string; sourceType: string }> = React.memo(
  function MemorySourceDisplay({ source, sourceType }) {
    if (!source) return null;

    // Fallback for old messages without sourceType: infer from source string
    let effectiveSourceType = sourceType;
    if (!effectiveSourceType) {
      if (source.includes("added by @")) {
        effectiveSourceType = "UI_ADD";
      } else if (source.includes("conversation with") || source.includes("Slack conversations") || source.includes("Discord conversations")) {
        effectiveSourceType = "UI"; // or could be SLACK/DISCORD, but UI is good enough
      } else if (source.includes("dashboard")) {
        effectiveSourceType = "DASHBOARD";
      } else if (source.includes("document")) {
        effectiveSourceType = "DOCUMENT";
      } else if (source.includes("incident channel")) {
        effectiveSourceType = "INCIDENT_CHANNEL";
      } else {
        effectiveSourceType = "SYSTEM";
      }
    }

    const { icon, label } = getSourceIconFromType(effectiveSourceType);

    // Parse hyperlinks in format [name|url]
    const hyperlinkRegex = /\[([^|\]]+)\|([^\]]+)\]/g;
    const parts: (string | JSX.Element)[] = [];
    let lastIndex = 0;
    let match;

    while ((match = hyperlinkRegex.exec(source)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(source.substring(lastIndex, match.index));
      }

      // Add hyperlink
      const [, name, url] = match;
      parts.push(
        <a
          key={match.index}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:text-primary/80 underline"
        >
          {name}
        </a>
      );

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < source.length) {
      parts.push(source.substring(lastIndex));
    }

    return (
      <span className="inline-flex items-baseline gap-1">
        <span title={label} className="inline-block align-baseline">
          {icon}
        </span>
        {(parts.length > 0 || source) && <span>({parts.length > 0 ? parts : source})</span>}
      </span>
    );
  }
);

interface MemoryItemProps {
  memory: MemoryAttribution;
  index: number;
  conversationId?: string;
  messageId?: string;
  onEditMemory?: (memoryId: string, memoryBody: MemoryBody) => void;
  isDeprecated: boolean;
  isV2?: boolean;  // If true, this is from LlmConversation/LlmMessage (V2)
  isActive: boolean;
  onMemoryClick?: (memoryId: string) => void;
}

const MemoryItem: React.FC<MemoryItemProps> = React.memo(function MemoryItem({
  memory,
  index,
  conversationId,
  messageId,
  onEditMemory,
  isDeprecated,
  isV2,
  isActive,
  onMemoryClick,
}) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [showOverlay, setShowOverlay] = React.useState(false);
  const [isCopied, setIsCopied] = React.useState(false);
  const copyTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const { handleSubmitFeedback } = useSubmitMemoryAttributionFeedbackMutation();

  // Convert index to letter (0->a, 1->b, 2->c, etc.)
  const getLetter = (idx: number) => String.fromCharCode(97 + idx);

  // Storage key for this specific memory feedback
  const storageKey = `memory_feedback_${memory.memoryId}_${messageId}`;

  // Load initial feedback state from localStorage
  const [feedbackGiven, setFeedbackGiven] = React.useState<"relevant" | "not_relevant" | null>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(storageKey);
      if (stored === "relevant" || stored === "not_relevant") {
        return stored;
      }
    }
    return null;
  });

  const handleFeedback = (type: "relevant" | "not_relevant") => {
    // Check if we have required IDs
    if (!conversationId || !messageId) {
      console.error("Missing conversationId or messageId for memory attribution feedback");
      return;
    }

    // Toggle behavior - clicking again cancels
    if (feedbackGiven === type) {
      setFeedbackGiven(null);
      localStorage.removeItem(storageKey);
    } else {
      const feedbackType: MemoryAttributionFeedbackType = type === "relevant" ? "RELEVANT" : "NOT_RELEVANT";

      handleSubmitFeedback(memory.memoryId, conversationId, messageId, feedbackType, () => {
        // On success, update local state and storage
        setFeedbackGiven(type);
        localStorage.setItem(storageKey, type);
      }, { isV2 });
    }
  };

  const handleCopyMemoryId = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(memory.memoryId);
      setIsCopied(true);

      // Clear any existing timer before setting a new one
      if (copyTimerRef.current) {
        clearTimeout(copyTimerRef.current);
      }

      copyTimerRef.current = setTimeout(() => {
        setIsCopied(false);
        copyTimerRef.current = null;
      }, 2000);
    } catch (err) {
      console.error("Failed to copy memory ID:", err);
    }
  };

  // Cleanup timer on unmount
  React.useEffect(() => {
    return () => {
      if (copyTimerRef.current) {
        clearTimeout(copyTimerRef.current);
      }
    };
  }, []);

  // Convert memory body to an object for ReactJson
  const getMemoryBodyAsObject = () => {
    const body = memory.memoryBody;
    const obj: Record<string, any> = {};

    if (body.__typename === "GraphQLGenericLearningBody") {
      if (body.type) obj.type = body.type;
      if (body.scope) obj.scope = body.scope;
      if (body.knowledge) obj.knowledge = body.knowledge;
      if (body.reasoning) obj.reasoning = body.reasoning;
    } else if (body.__typename === "GraphQLQueryBody") {
      if (body.queryType) obj.queryType = body.queryType;
      // Prefer queryDescription, fall back to question for migration compatibility
      if (body.queryDescription) {
        obj.queryDescription = body.queryDescription;
      } else if (body.question) {
        obj.question = body.question;
      }
      if (body.query) obj.query = body.query;
    } else if (body.__typename === "GraphQLResourceBody") {
      if (body.resourceType) obj.resourceType = body.resourceType;
      if (body.name) obj.name = body.name;
      if (body.content) obj.content = body.content;
    }

    return obj;
  };

  // Check if there's any body content to display
  const memoryBodyPreview = Object.keys(getMemoryBodyAsObject()).length > 0;

  // Get letter for this memory's index (a, b, c, etc.)
  const letter = String.fromCharCode(97 + index);

  const handleClick = onMemoryClick ? () => onMemoryClick(memory.memoryId) : undefined;

  const handleKeyDown = onMemoryClick
    ? (e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onMemoryClick(memory.memoryId);
        }
      }
    : undefined;

  return (
    <div
      id={`memory-${messageId}-${letter}`}
      className={cn(
        "flex flex-col w-full items-start p-2 rounded text-xs",
        isActive ? "bg-green-900/50 text-white" : "text-gray-400",
        onMemoryClick && "cursor-pointer"
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={onMemoryClick ? "button" : undefined}
      tabIndex={onMemoryClick ? 0 : undefined}
    >
      {/* Top: Summary + Source + Influence Reason */}
      <div className="flex items-start w-full">
        <div className="flex items-center gap-1 mr-1">
          <span className="text-green-400 font-semibold text-xs">[{getLetter(index)}]</span>
          <Tooltip content="Copy memory ID" delay={50}>
            <button
              onClick={handleCopyMemoryId}
              className="p-0.5 rounded hover:bg-gray-700/50 transition-colors inline-flex items-center"
              aria-label="Copy memory ID"
            >
              {isCopied ? (
                <Check className="w-3 h-3 text-green-400" />
              ) : (
                <Copy className="w-3 h-3 text-gray-400 hover:text-gray-300" />
              )}
            </button>
          </Tooltip>
        </div>
        <div className="break-words flex-1">
          <p className="last:mb-0">
            {memory.source && (
              <>
                <MemorySourceDisplay source={memory.source} sourceType={memory.sourceType} />{" "}
              </>
            )}
            {memory.memorySummary}
            {memory.influenceReason && (
              <>
                {" "}
                {memory.influenceReason.endsWith(".") ? memory.influenceReason : `${memory.influenceReason}.`}
              </>
            )}
          </p>
        </div>
      </div>

      {/* Bottom: Expandable memory body preview */}
      {memoryBodyPreview && (
        <div className="mt-2 w-full">
          <div
            className={cn(
              "w-full rounded-lg overflow-hidden bg-card flex flex-col items-center border border-border relative p-1 cursor-pointer memory-box",
              !isExpanded && "max-h-[8rem]",
              "hover:border-border"
            )}
            onClick={() => {
              setIsExpanded(!isExpanded);
              setShowOverlay(false);
            }}
            onMouseEnter={() => setShowOverlay(true)}
            onMouseLeave={() => setShowOverlay(false)}
          >
            <div className={cn(
              "flex-1 w-full flex items-center justify-center bg-transparent transition-colors duration-200",
              !isExpanded ? "overflow-hidden" : "overflow-visible"
            )}>
              <div className={cn("w-full", !isExpanded ? "h-full overflow-auto" : "overflow-visible")}>
                <ReactJson
                  src={getMemoryBodyAsObject()}
                  theme="twilight"
                  collapsed={isExpanded ? false : 2}
                  displayDataTypes={false}
                  displayObjectSize={false}
                  enableClipboard={false}
                  quotesOnKeys={false}
                  displayArrayKey={false}
                  name={false}
                  groupArraysAfterLength={100}
                  shouldCollapse={(field) => {
                    if (!isExpanded) {
                      // When collapsed, collapse nested objects/arrays (depth > 1)
                      return field.namespace.length > 1;
                    }
                    return false;
                  }}
                  style={{
                    fontSize: "10px",
                    wordBreak: "break-all",
                  }}
                />
              </div>
            </div>

            {/* Hover overlay with icon */}
            <div className={cn(
              "absolute inset-0 flex items-center justify-center bg-black/20 transition-opacity pointer-events-none",
              showOverlay ? "opacity-100" : "opacity-0"
            )}>
              {!isExpanded ? (
                <Maximize2 className="text-white/70 w-6 h-6" />
              ) : (
                <Minimize2 className="text-white/70 w-6 h-6" />
              )}
            </div>

            {/* Action buttons or deprecation warning - shown only when expanded */}
            {isExpanded && (
              <div className="w-full px-2 pb-2 pt-3">
                {isDeprecated ? (
                  <div className="flex items-center justify-end">
                    <div className="text-xs text-amber-400 italic">
                      This memory has been updated. View the latest version in Memory Explorer.
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFeedback("relevant");
                      }}
                      className={cn(
                        "flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs transition-colors",
                        feedbackGiven === "relevant"
                          ? "bg-green-900/50 text-green-400"
                          : "text-gray-400 hover:text-green-400 hover:bg-green-900/30"
                      )}
                      title="Mark as relevant"
                    >
                      <CheckCircle2 className="w-3 h-3" />
                      <span>Relevant</span>
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFeedback("not_relevant");
                      }}
                      className={cn(
                        "flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs transition-colors",
                        feedbackGiven === "not_relevant"
                          ? "bg-amber-900/50 text-amber-400"
                          : "text-gray-400 hover:text-amber-400 hover:bg-amber-900/30"
                      )}
                      title="Mark as not relevant"
                    >
                      <XCircle className="w-3 h-3" />
                      <span>Not Relevant</span>
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onEditMemory) {
                          onEditMemory(memory.memoryId, memory.memoryBody);
                        }
                      }}
                      className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs text-gray-400 hover:text-primary hover:bg-primary/10 transition-colors"
                      title="Fix memory"
                    >
                      <Edit2 className="w-3 h-3" />
                      <span>Fix</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

export const ReferencedMemoriesSection: React.FC<
  ReferencedMemoriesSectionProps
> = ({ memoryAttributions, conversationId, messageId, onEditMemory, isV2, activeMemoryId, onMemoryClick }) => {
  const [isExpanded, setIsExpanded] = React.useState(true);

  // Auto-expand when a memory is clicked
  React.useEffect(() => {
    if (activeMemoryId) {
      setIsExpanded(true);
      setTimeout(() => {
        // Find the index of the active memory to convert to letter
        const activeIndex = memoryAttributions.findIndex(m => m.memoryId === activeMemoryId);
        if (activeIndex !== -1) {
          const letter = String.fromCharCode(97 + activeIndex);
          const selectedElement = document.getElementById(`memory-${messageId}-${letter}`);
          if (selectedElement) {
            selectedElement.scrollIntoView({ behavior: "smooth", block: "nearest" });
          }
        }
      }, 100);
    }
  }, [activeMemoryId, memoryAttributions, messageId]);

  if (!memoryAttributions || memoryAttributions.length === 0) return null;

  return (
    <div className="mt-4 pt-3 border-t border-border">
      <div
        className="text-green-400 text-xs uppercase font-semibold tracking-wide mb-2 flex items-center cursor-pointer hover:text-green-300 transition-colors"
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        <svg
          className={`w-4 h-4 text-green-400 transform transition-transform mr-1 ${isExpanded ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
        <span>Referenced Context ({memoryAttributions.length})</span>
      </div>

      {isExpanded && (
        <div className="space-y-2">
          {memoryAttributions.map((memory, i) => {
            // Check if memory is deprecated based on backend status
            const isDeprecated = memory.status === "deprecated";

            return (
              <MemoryItem
                key={memory.memoryId}
                memory={memory}
                index={i}
                conversationId={conversationId}
                messageId={messageId}
                onEditMemory={onEditMemory}
                isDeprecated={isDeprecated}
                isV2={isV2}
                isActive={activeMemoryId === memory.memoryId}
                onMemoryClick={onMemoryClick}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};
